import Swal from "sweetalert2";
import ApiService, { HttpMethod } from "../../../../commons/api/api_service";
import BaseResponse from "../../../../commons/api/base_response";
import { Endpoints } from "../../../../commons/api/endpoints";
import { ProviderVM } from "../../../../commons/base/providers/view_model";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../../../commons/scaffold/scaffold";
import {
    ItemOptions,
    LocationModel,
    SubmitToVendorAdjustmentModel,
    SubmitToVendorAdjustmentResponse,
    ToVendorAdjustmentModel,
    ToVendorDetailResponse,
    ToVendorResponse,
} from "../models/adjustment_forms_to_vendor_model";
import StringUtils from "../../../../commons/utils/string_utils";

export default class AdjustmentFormsToVendorViewModel extends ProviderVM {
    // UI States
    isLoading: boolean = false;
    errorMessage: string = "";
    showSnackbar?: ShowSnackbarModel;
    showErrorView?: ShowErrorViewModel;
    isShowModal: boolean = false;

    // Data States
    transferToVendorList: ToVendorResponse[] = [];
    filteredTransferToVendorList: ToVendorResponse[] = [];
    locationOptions: LocationModel[] = [];
    filterLocationId?: number;
    vendorsOptions: LocationModel[] = [];
    itemOptions: ItemOptions[] = [];
    selectedItemId?: number;
    addedItemIds: number[] = [];
    searchQuery: string = "";

    // Submit Data
    formId?: number;
    formCode?: string;
    formType: string = "TOVENDOR";
    newFromLocationId?: number;
    newToLocationId?: number;
    formSentAt?: string;
    transferToVendorDataMap: Map<string, ToVendorAdjustmentModel> = new Map<
        string,
        ToVendorAdjustmentModel
    >();

    constructor() {
        super();
        this.setup();
    }

    setup() {
        this.fetchTransferToVendorList();
        this.fetchLocationOptions();
        this.fetchVendorsOptions();
        this.fetchItemOptions();
    }

    // Fetch Data
    fetchLocationOptions() {
        ApiService.fetch(Endpoints.adjustmentFormGetLocationOptions, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<LocationModel[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    this.setLocationOptions(response.data as LocationModel[]);
                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                        this.setup();
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    fetchVendorsOptions() {
        ApiService.fetch(Endpoints.adjustmentFormGetVendorsOptions, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<LocationModel[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    this.setVendorOptions(response.data as LocationModel[]);
                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                        this.setup();
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    fetchTransferToVendorList(filterLocationId?: number) {
        this.setIsLoading(true);
        ApiService.fetchWithQueryParams(
            Endpoints.adjustmentFormGetToVendorList,
            {
                httpMethod: HttpMethod.get,
                queryParameters: {
                    ...(this.filterLocationId && {
                        fromLocationId: filterLocationId,
                    }),
                },
                onSuccess: (response: BaseResponse<ToVendorResponse[]>) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        const toVendorList =
                            response.data as ToVendorResponse[];
                        this.setTransferToVendorList(toVendorList);
                        this.setFilteredTransferToVendorList(toVendorList);
                    } else {
                        console.log(response);
                        this.setErrorMessage(response.message);
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                                this.setup();
                            },
                            message: this.errorMessage,
                        });
                    }
                },
                onFailure: (error) => {
                    this.setIsLoading(false);
                    console.log(error);
                    this.setErrorMessage(
                        "Failed fetching data, please refresh or try again"
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    fetchTransferToVendorDetail(toVendorId: number) {
        this.setIsLoading(true);
        ApiService.fetch(
            `${Endpoints.adjustmentFormGetToVendorDetail}/${toVendorId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<ToVendorDetailResponse[]>
                ) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        const items = response.data as ToVendorDetailResponse[];
                        const map = new Map<string, ToVendorAdjustmentModel>();
                        const addedItemIds: number[] = [];
                        items.forEach((item) => {
                            const uniqueId = `${item.rowId}-${item.masterItemId}`;
                            map.set(uniqueId, {
                                rowId: item.rowId,
                                itemId: item.masterItemId,
                                uniqueId: uniqueId,
                                isNew: false,
                                bigQty: item.qtyBigUnit,
                                smallQty: item.qtySmallUnit,
                                itemName: item.masterItemName, // only to show in modal
                                bigUnit: item.bigUnit, // only to show in modal
                                smallUnit: item.smallUnit, // only to show in modal
                            });
                            addedItemIds.push(item.masterItemId);
                        });
                        this.setTransferToVendorDataMap(map);
                        this.setAddedItemIds(addedItemIds);
                    } else {
                        console.log(response);
                        this.setErrorMessage(response.message);
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                                this.setup();
                            },
                            message: this.errorMessage,
                        });
                    }
                },
                onFailure: (error) => {
                    console.log(error);
                    this.setIsLoading(false);
                    this.setErrorMessage(
                        "Failed fetching data, please refresh or try again"
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    fetchItemOptions() {
        this.setIsLoading(true);
        ApiService.fetch(Endpoints.adjustmentFormGetItemOptions, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<ItemOptions[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    const itemOptions = response.data as ItemOptions[];
                    this.setItemOptions(itemOptions);
                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setup();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setup();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    // Data Setters
    setLocationOptions(value: LocationModel[]) {
        this.emit(() => {
            this.locationOptions = value;
        });
    }

    setVendorOptions(value: LocationModel[]) {
        this.emit(() => {
            this.vendorsOptions = value;
        });
    }

    setFilterLocationId(value?: number) {
        this.emit(() => {
            this.filterLocationId = value;
        });
    }

    setTransferToVendorList(value: ToVendorResponse[]) {
        this.emit(() => {
            this.transferToVendorList = value;
        });
    }

    setFilteredTransferToVendorList(value: ToVendorResponse[]) {
        this.emit(() => {
            this.filteredTransferToVendorList = value;
        });
    }

    setItemOptions(value: ItemOptions[]) {
        this.emit(() => {
            this.itemOptions = value;
        });
    }

    setSelectedItemId(value?: number) {
        this.emit(() => {
            this.selectedItemId = value;
        });
    }

    setAddedItemIds(value: number[]) {
        this.emit(() => {
            this.addedItemIds = value;
        });
    }

    setSearchQuery(value: string) {
        this.emit(() => {
            this.searchQuery = value;
        });
    }

    setFormId(value?: number) {
        this.emit(() => {
            this.formId = value;
        });
    }

    setFormCode(value?: string) {
        this.emit(() => {
            this.formCode = value;
        });
    }

    setFormSentAt(value?: string) {
        let adjustedDate: string | undefined;
        if (value) {
            adjustedDate = StringUtils.UTCtoLocale(value);
        }
        this.emit(() => {
            this.formSentAt = adjustedDate;
        });
    }

    setNewFromLocationId(value?: number) {
        this.emit(() => {
            this.newFromLocationId = value;
        });
    }

    setNewToLocationId(value?: number) {
        this.emit(() => {
            this.newToLocationId = value;
        });
    }

    setTransferToVendorDataMap(value: Map<string, ToVendorAdjustmentModel>) {
        this.emit(() => {
            this.transferToVendorDataMap = value;
        });
    }

    handleChangeItemData(
        uniqueId: string,
        field: keyof ToVendorAdjustmentModel,
        value: number
    ) {
        const updatedMap = new Map(this.transferToVendorDataMap);
        const oldData = updatedMap.get(uniqueId);
        if (oldData) {
            updatedMap.set(uniqueId, { ...oldData, [field]: value });
            this.setTransferToVendorDataMap(updatedMap);
        }
    }

    handleAddItem(itemId: number) {
        if (!this.addedItemIds.includes(itemId)) {
            const updatedMap = new Map(this.transferToVendorDataMap);
            const selectedOption = this.itemOptions.find(
                (option) => option.id === itemId
            );

            const randomNumber = Math.random();
            const uniqueId = `${randomNumber}-${itemId}`;
            updatedMap.set(uniqueId, {
                rowId: randomNumber,
                itemId: itemId,
                uniqueId: uniqueId,
                isNew: true,
                bigQty: 0,
                smallQty: 0,
                itemName: selectedOption?.item, // only to show in modal
                bigUnit: selectedOption?.bigUnit, // only to show in modal
                smallUnit: selectedOption?.smallUnit, // only to show in modal
            });
            this.setAddedItemIds([...this.addedItemIds, itemId]);
            this.setTransferToVendorDataMap(updatedMap);
            this.setSearchQuery(selectedOption?.item.toLowerCase() ?? "");
        }
    }

    handleSubmit() {
        Swal.fire({
            title: "Konfirmasi",
            text: "Anda yakin ingin melakukan adjustment ini?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Gass",
        }).then((result) => {
            if (result.isConfirmed) {
                if (
                    !this.formId ||
                    !this.formCode ||
                    !this.formSentAt ||
                    !this.newFromLocationId ||
                    !this.newToLocationId
                ) {
                    this.setShowErrorView({
                        message: "Data belum lengkap!",
                        action: () => {
                            this.setShowErrorView(undefined);
                        },
                    });
                    return;
                }

                this.setIsLoading(true);

                const items = [...this.transferToVendorDataMap.values()].map(
                    (value): ToVendorAdjustmentModel => ({
                        rowId: value.rowId,
                        itemId: value.itemId,
                        uniqueId: value.uniqueId,
                        isNew: value.isNew,
                        bigQty: value.bigQty,
                        smallQty: value.smallQty,
                    })
                );

                const submitData: SubmitToVendorAdjustmentModel = {
                    formId: this.formId,
                    formCode: this.formCode,
                    formType: "TOVENDOR",
                    sentAt: StringUtils.LocaleToUTC(this.formSentAt),
                    newLocationFrom: this.newFromLocationId,
                    newLocationTo: this.newToLocationId,
                    items,
                };

                ApiService.fetch(Endpoints.adjustmentFormSubmit, {
                    httpMethod: HttpMethod.post,
                    parameters: submitData,
                    onSuccess: (
                        response: BaseResponse<SubmitToVendorAdjustmentResponse>
                    ) => {
                        this.setIsLoading(false);
                        if (response.code === 200) {
                            const data =
                                response.data as SubmitToVendorAdjustmentResponse;
                            console.log(data);
                            this.showSnackbar = new ShowSnackbarModel({
                                message: "Success Adjust Receipt Form",
                                type: "normal",
                            });
                        } else {
                            console.log(response);
                            this.setErrorMessage(response.message);
                            this.setShowErrorView({
                                action: () => {
                                    this.reset();
                                    this.setup();
                                    this.setShowErrorView(undefined);
                                    this.setErrorMessage("");
                                },
                                message: this.errorMessage,
                            });
                        }
                        this.resetModal();
                        this.setup();
                    },
                    onFailure: (error) => {
                        console.log(error);
                        this.setIsLoading(false);
                        this.setErrorMessage(
                            "Failed fetching data, please refresh or try again"
                        );
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setup();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                            },
                            message: this.errorMessage,
                        });
                    },
                });
            }
        });
    }

    // UI Setters
    setIsLoading(value: boolean) {
        this.emit(() => {
            this.isLoading = value;
        });
    }

    setErrorMessage(value: string) {
        this.emit(() => {
            this.errorMessage = value;
        });
    }

    setShowErrorView(value?: ShowErrorViewModel) {
        this.emit(() => {
            this.showErrorView = value;
        });
    }

    setShowSnackbar(value: ShowSnackbarModel) {
        this.emit(() => {
            this.showSnackbar = value;
        });
    }

    setIsShowModal(value: boolean) {
        this.emit(() => {
            this.isShowModal = value;
        });
    }

    // Reset
    resetModal() {
        this.emit(() => {
            this.isShowModal = false;
            this.formId = undefined;
            this.formCode = undefined;
            this.newFromLocationId = undefined;
            this.newToLocationId = undefined;
            this.transferToVendorDataMap = new Map<
                string,
                ToVendorAdjustmentModel
            >();
            this.searchQuery = "";
        });
    }

    reset() {
        this.emit(() => {
            this.isLoading = false;
            this.errorMessage = "";
            this.showSnackbar = undefined;
            this.showErrorView = undefined;

            this.transferToVendorList = [];
            this.filteredTransferToVendorList = [];
            this.filterLocationId = undefined;
            this.locationOptions = [];
            this.itemOptions = [];
            this.selectedItemId = undefined;

            this.isShowModal = false;
            this.formId = undefined;
            this.formCode = undefined;
            this.newFromLocationId = undefined;
            this.newToLocationId = undefined;
            this.transferToVendorDataMap = new Map<
                string,
                ToVendorAdjustmentModel
            >();
        });
    }
}
